import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePersonDetails } from '../hooks/usePersonQueries';
import { navigateToContent } from '../utils/navigation';

const PersonDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: person, isLoading, error } = usePersonDetails(id || '');

  const handleBackClick = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading person details...</div>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">Person not found</div>
          <button onClick={handleBackClick} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const age = person.birthYear ? (person.deathYear || currentYear) - person.birthYear : null;

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={handleBackClick}
          className="mb-6 flex items-center text-gray-300 hover:text-white transition-colors duration-200"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Person Image and Basic Info */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-6 sticky top-6">
              <img
                src={person.imageUrl}
                alt={person.name}
                className="w-full rounded-lg mb-4 aspect-[3/4] object-cover"
              />
              
              <h1 className="text-2xl font-bold text-white mb-4">{person.name}</h1>
              
              <div className="space-y-3 text-sm">
                {person.birthYear && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Born</span>
                    <span className="text-white">
                      {person.birthYear}
                      {age && ` (${person.deathYear ? 'died at' : 'age'} ${age})`}
                    </span>
                  </div>
                )}
                
                {person.deathYear && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Died</span>
                    <span className="text-white">{person.deathYear}</span>
                  </div>
                )}
                
                {person.primaryProfession.length > 0 && (
                  <div>
                    <span className="text-gray-400 block mb-2">Known For</span>
                    <div className="flex flex-wrap gap-2">
                      {person.primaryProfession.map((profession) => (
                        <span
                          key={profession}
                          className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-xs capitalize"
                        >
                          {profession.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Biography */}
            {person.biography && (
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Biography</h2>
                <p className="text-gray-300 leading-relaxed">{person.biography}</p>
              </div>
            )}

            {/* Known For Titles */}
            {person.knownForTitles.length > 0 && (
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Known For</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {person.knownForTitles.map((title) => (
                    <div
                      key={title.id}
                      className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors duration-200 cursor-pointer"
                      onClick={() => navigateToContent(navigate, title)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="w-16 h-24 flex-shrink-0">
                          <img
                            src={title.imageUrl}
                            alt={title.title}
                            className="w-full h-full object-contain rounded bg-gray-800"
                            onError={(e) => {
                              // Fallback to placeholder if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          <div className="w-16 h-24 bg-gray-600 rounded flex items-center justify-center hidden">
                            <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm3 2h6v4H7V5zm8 8v2h1v-2h-1zm-2-2H7v4h6v-4zm2 0h1V9h-1v2zm1-4V5h-1v2h1zM5 5v6H4V5h1zm0 8H4v2h1v-2z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium truncate">{title.title}</div>
                          <div className="text-gray-400 text-sm">{title.year}</div>
                          {title.rating && (
                            <div className="flex items-center mt-1">
                              <svg className="w-3 h-3 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span className="text-gray-400 text-xs">{title.rating}</span>
                            </div>
                          )}
                          {title.genres.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {title.genres.slice(0, 2).map((genre) => (
                                <span key={genre} className="px-2 py-1 bg-gray-600 text-gray-300 rounded text-xs">
                                  {genre}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Filmography */}
            {person.filmography && person.filmography.length > 0 ? (
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Filmography</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {person.filmography.map((title) => (
                    <div
                      key={title.id}
                      className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors duration-200 cursor-pointer"
                      onClick={() => navigateToContent(navigate, title)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="w-16 h-24 flex-shrink-0">
                          <img
                            src={title.imageUrl}
                            alt={title.title}
                            className="w-full h-full object-contain rounded bg-gray-800"
                            onError={(e) => {
                              // Fallback to placeholder if image fails to load
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                          <div className="w-16 h-24 bg-gray-600 rounded flex items-center justify-center hidden">
                            <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm3 2h6v4H7V5zm8 8v2h1v-2h-1zm-2-2H7v4h6v-4zm2 0h1V9h-1v2zm1-4V5h-1v2h1zM5 5v6H4V5h1zm0 8H4v2h1v-2z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium truncate">{title.title}</div>
                          <div className="text-gray-400 text-sm">{title.year}</div>
                          {title.rating && (
                            <div className="flex items-center mt-1">
                              <svg className="w-3 h-3 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span className="text-gray-400 text-xs">{title.rating}</span>
                            </div>
                          )}
                          {title.genres.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {title.genres.slice(0, 2).map((genre) => (
                                <span key={genre} className="px-2 py-1 bg-gray-600 text-gray-300 rounded text-xs">
                                  {genre}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Filmography</h2>
                <div className="text-gray-400 text-center py-8">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
                  </svg>
                  <p>No filmography available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonDetailsPage;